import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddCardIcon from '@mui/icons-material/AddCard';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { DATABASE } from '../../Config/firebase';
import { useNavigate } from 'react-router-dom';

/**
 * NotificationBell shows pending withdrawals + deposits that need admin attention.
 * Count from transection_requests: withdrawals (type 1, status 0/1/2) + deposits (type 0, status 0/1).
 */
const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);
  const [pendingDeposits, setPendingDeposits] = useState(0);
  const navigate = useNavigate();

  const pendingCount = pendingWithdrawals + pendingDeposits;

  useEffect(() => {
    // Pending withdrawals: request_type=1 (withdrawal), request_status in [0,1,2] (Pending, OTP verified, Approved)
    const withdrawalsQuery = query(
      collection(DATABASE, 'transection_requests'),
      where('request_type', '==', 1),
      where('request_status', 'in', [0, 1, 2])
    );

    const unsubWithdrawals = onSnapshot(withdrawalsQuery, (snapshot) => {
      setPendingWithdrawals(snapshot.size);
    }, (error) => {
      console.error('Error fetching pending withdrawals:', error);
    });

    // Pending deposits: request_type=0 (deposit), request_status in [0,1] (Pending, Processing)
    const depositsQuery = query(
      collection(DATABASE, 'transection_requests'),
      where('request_type', '==', 0),
      where('request_status', 'in', [0, 1])
    );

    const unsubDeposits = onSnapshot(depositsQuery, (snapshot) => {
      setPendingDeposits(snapshot.size);
    }, (error) => {
      console.error('Error fetching pending deposits:', error);
    });

    return () => {
      unsubWithdrawals();
      unsubDeposits();
    };
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewWithdrawals = () => {
    navigate('/admin/withdrawfunds');
    handleClose();
  };

  const handleViewDeposits = () => {
    navigate('/admin/fundsdeposit');
    handleClose();
  };

  const handleViewNotifications = () => {
    navigate('/admin/notifications');
    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: '#ADADAD',
          '&:hover': {
            color: '#2af598'
          }
        }}
      >
        <Badge badgeContent={pendingCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            mt: 1.5,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: '8px'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Pending Actions
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pendingCount} item{pendingCount !== 1 ? 's' : ''} need attention
          </Typography>
        </Box>

        <Divider />

        {pendingCount === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No pending withdrawals or deposits
            </Typography>
          </Box>
        ) : (
          <>
            <MenuItem onClick={handleViewWithdrawals} sx={{ py: 1.5, px: 2 }}>
              <AccountBalanceWalletIcon sx={{ mr: 1.5, color: '#f57c00' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Withdrawals
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {pendingWithdrawals} pending
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={handleViewDeposits} sx={{ py: 1.5, px: 2 }}>
              <AddCardIcon sx={{ mr: 1.5, color: '#388e3c' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Deposits
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {pendingDeposits} pending
                </Typography>
              </Box>
            </MenuItem>

            <Divider />

            <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                size="small"
                onClick={handleViewWithdrawals}
                sx={{ textTransform: 'none' }}
              >
                Withdrawals
              </Button>
              <Button
                fullWidth
                size="small"
                onClick={handleViewDeposits}
                sx={{ textTransform: 'none' }}
              >
                Deposits
              </Button>
            </Box>
          </>
        )}

        <Divider />

        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button
            fullWidth
            onClick={handleViewNotifications}
            sx={{
              textTransform: 'none',
              color: '#2af598',
              fontWeight: 600
            }}
          >
            View All Notifications
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationBell;
